import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Modal } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { QuestionTemplate, User, SortConfig, QuestionItem } from '../../../types';
import { fetchQuestionTemplates, deleteQuestionTemplate } from '../../../api/questionTemplateApi';
import { getSortIcon } from '../../../utils/Utils';
import Header from '../layout/Header';
import QuestionTemplateForm from './QuestionTemplateForm';
import '../candidate/CandidateList.css';
import './QuestionTemplateList.css';

const QuestionTemplateList: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [questionTemplates, setQuestionTemplates] = useState<QuestionTemplate[]>([]);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [detailTemplate, setDetailTemplate] = useState<QuestionTemplate | null>(null);
  const [updateTemplate, setUpdateTemplate] = useState<QuestionTemplate | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'id', direction: 'asc' });
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setUser(userData);
      } catch (error: unknown) {
        console.error('Error parsing user data:', error);
        navigate('/');
      }
    } else {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    loadQuestionTemplates();
  }, []);

  const loadQuestionTemplates = async (): Promise<void> => {
    try {
      setLoading(true);
      const data = await fetchQuestionTemplates();
      setQuestionTemplates(data);
      setError('');
    } catch (err) {
      setError('質問テンプレートの取得中にエラーが発生しました。');
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = (): void => {
    setUpdateTemplate(null);
    setShowCreateModal(true);
  };

  const handleEdit = (template: QuestionTemplate): void => {
    if (template.user_id) {
      setUpdateTemplate(template);
      setShowCreateModal(true);
    }
  };

  const handleDelete = async (template: QuestionTemplate): Promise<void> => {
    if (!template.user_id) return;

    const confirmed = window.confirm(`「${template.name}」を削除してもよろしいですか？`);
    if (!confirmed) return;

    try {
      await deleteQuestionTemplate(template.id);
      alert(`「${template.name}」を削除しました。`);

      await loadQuestionTemplates();
    } catch (err) {
      alert(`「${template.name}」の削除が失敗しました。`);
      console.error('Error deleting template:', err);
    }
  };

  const handleFormSuccess = (): void => {
    setShowCreateModal(false);
    setUpdateTemplate(null);
    loadQuestionTemplates();
  };

  const handleDetail = (template: QuestionTemplate): void => {
    setDetailTemplate(template);
    setShowDetailModal(true);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (key: string): void => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (filter: string): void => {
    setActiveFilter(filter);
  };

  const isEditable = (template: QuestionTemplate): boolean => {
    return template.user_id !== null && template.user_id !== undefined;
  };

  const getTemplateType = (template: QuestionTemplate): string => {
    return isEditable(template) ? '企業固有' : '共通';
  };

  const getQuestionCount = (template: QuestionTemplate): number => {
    return template.questions ? template.questions.length : 0;
  };

  const sortedTemplates = React.useMemo(() => {
    let filtered = questionTemplates.filter(template =>
      template.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (activeFilter === 'common') {
      filtered = filtered.filter(template => !isEditable(template));
    } else if (activeFilter === 'client') {
      filtered = filtered.filter(template => isEditable(template));
    }

    if (!sortConfig.key) return filtered;

    return [...filtered].sort((a: QuestionTemplate, b: QuestionTemplate) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortConfig.key) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'type':
          aValue = getTemplateType(a);
          bValue = getTemplateType(b);
          break;
        case 'question_count':
          aValue = getQuestionCount(a);
          bValue = getQuestionCount(b);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [questionTemplates, searchTerm, sortConfig, activeFilter]);

  const filteredTemplates = sortedTemplates;

  if (loading) {
    return (
      <div className="dashboard-container">
        <Header user={user} />
        <Container>
          <div className="text-center" style={{ marginTop: '50px' }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">情報を読み込んでいます...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Header user={user} />

      <Container>
        <div className="search-container">
          <div className="status-filter-container">
            <div 
              className={`status-filter-label ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => handleFilterChange('all')}
            >
              全て <span className="status-count">({questionTemplates.length})</span>
            </div>
            <div 
              className={`status-filter-label ${activeFilter === 'common' ? 'active' : ''}`}
              onClick={() => handleFilterChange('common')}
            >
              共通 <span className="status-count">({questionTemplates.filter(t => !isEditable(t)).length})</span>
            </div>
            <div 
              className={`status-filter-label ${activeFilter === 'client' ? 'active' : ''}`}
              onClick={() => handleFilterChange('client')}
            >
              企業固有 <span className="status-count">({questionTemplates.filter(t => isEditable(t)).length})</span>
            </div>
          </div>

          <div className="search-input-container">
            <Form>
              <Form.Control
                type="text"
                placeholder="テンプレート名"
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
            </Form>
          </div>

          <div className="create-button-container">
            <Button 
              variant="success" 
              className="create-button"
              onClick={handleCreate}
            >
              新規テンプレート作成
            </Button>
          </div>
        </div>

        <div className='reference-check-text'>
          質問テンプレート一覧
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">{error}</Alert>
        )}

        <div className="candidates-table-container">
          <table className="candidates-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className="sortable text-left">
                  テンプレート名 {getSortIcon('name', sortConfig)}
                </th>
                <th onClick={() => handleSort('type')} className="sortable text-center">
                  区分 {getSortIcon('type', sortConfig)}
                </th>
                <th onClick={() => handleSort('question_count')} className="sortable text-center">
                  質問数 {getSortIcon('question_count', sortConfig)}
                </th>
                <th className="text-center">操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.length > 0 ? (
                filteredTemplates.map((template) => (
                  <tr key={template.id}>
                    <td className="text-left">{template.name}</td>
                    <td className="text-center">
                      <span className={`status-badge ${isEditable(template) ? 'status-completed' : 'status-pending'}`}>
                        {isEditable(template) ? '企業固有' : '共通'}
                      </span>
                    </td>
                    <td className="text-center">
                      {template.questions ? template.questions.length : 0}問
                    </td>
                    <td className="text-center">
                      {isEditable(template) ? (
                        <>
                          <Button
                            className="detail-button me-2"
                            onClick={() => handleEdit(template)}
                          >
                            編集
                          </Button>
                          <Button
                            className="delete-button-icon"
                            onClick={() => handleDelete(template)}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </Button>
                        </>
                      ) : (
                        <Button
                          className="detail-button"
                          onClick={() => handleDetail(template)}
                        >
                          詳細
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center">該当するデータはありません。</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* ダッシュボードに戻るボタン */}
        <Link to="/dashboard" className="back-link mb-5">
          <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
          候補者一覧へ戻る
        </Link>
      </Container>

      <QuestionTemplateForm
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        template={updateTemplate}
        onSuccess={handleFormSuccess}
      />

      <Modal 
        show={showDetailModal} 
        onHide={() => setShowDetailModal(false)}
        size="lg" 
        centered
        className="question-template-detail-modal"
        backdrop="static"  // Prevent close when clicking outside
        keyboard={false}   // Optional: Prevent closing with ESC key
      >
        <Modal.Header closeButton>
          <Modal.Title>質問テンプレート詳細</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailTemplate && (
            <div>
              <div className="mb-3">
                <strong>{detailTemplate.name}</strong>
              </div>
              <div className="mb-1">
                <small>質問一覧:</small>
              </div>
              <div className="question-preview">
                {detailTemplate.questions && detailTemplate.questions.length > 0 ? (
                  detailTemplate.questions
                    .sort((a: QuestionItem, b: QuestionItem) => a.order - b.order)
                    .map((question, index) => (
                      <div key={`question-${index + 1}`} className="question-item" style={{ marginBottom: '0' }}>
                        <div className="question-text">
                          Q{index + 1}. {question.text.replace(/{candidate_name}/g, '候補者')}
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="text-center text-muted">質問が登録されていません</div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            閉じる
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default QuestionTemplateList;
